import { TextButton } from '@creditornot/cb-button';
import { Download, Plus } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import { useDroppable } from '@dnd-kit/core';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { DnD } from '../types';

type Props = {
  className?: string;
  onCreateArea: () => void;
  onCreateFromCategories: () => void;
};

const Root = styled.div`
  height: 100%;
  padding: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled(T.Heading4)`
  color: ${cbSemanticCssVars.textPrimary};
  margin-block-end: 12px;
`;

const Subtitle = styled(T.Body3)`
  text-align: center;
  color: ${cbSemanticCssVars.textSecondary};
  margin-block-end: 24px;
`;

const DropArea = styled.div<{ canDrop: boolean }>`
  ${typographyCss.Body3()};
  align-self: stretch;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 68px;
  padding: 24px;
  margin-block-end: 24px;
  color: ${({ canDrop }) => (canDrop ? cbSemanticCssVars.textPrimary : cbSemanticCssVars.textSecondary)};
  background-color: ${({ canDrop }) =>
    canDrop ? colors.wolt8 : cssThemedValue({ light: colors.pepper2, dark: colors.salt4 })};
  border: 2px dashed
    ${({ canDrop }) => (canDrop ? colors.wolt100 : cssThemedValue({ light: colors.pepper20, dark: colors.salt24 }))};
  border-radius: 8px;
`;

const GetStarted = ({ className, onCreateArea, onCreateFromCategories }: Props) => {
  const { setNodeRef, active } = useDroppable({ id: DnD.Dropzone, data: { type: DnD.Dropzone } });

  const canDrop = useMemo(() => {
    const type = active?.data.current?.type;
    return type === DnD.SingleItem || type === DnD.MultiItems;
  }, [active?.data]);

  return (
    <Root className={className}>
      <Title>
        <FormattedMessage id="picking-route.get-started-title" />
      </Title>
      <Subtitle>
        <FormattedMessage id="picking-route.get-started-subtitle" />
      </Subtitle>
      <DropArea ref={setNodeRef} canDrop={canDrop}>
        <FormattedMessage id="picking-route.drop-area-text" />
      </DropArea>
      <TextButton variant="blue" icon={<Plus />} style={{ marginBlockEnd: 18 }} onClick={onCreateArea}>
        <FormattedMessage id="picking-route.get-started-create-custom" />
      </TextButton>
      <TextButton variant="blue" icon={<Download />} onClick={onCreateFromCategories}>
        <FormattedMessage id="picking-route.get-started-create-from-categories" />
      </TextButton>
    </Root>
  );
};

export default GetStarted;
